// The book — one row per account, driven by the selected range + comparison
// mode. Sortable; default = the morning order (needs-attention first, then
// worst CPL change). A channel the account doesn't run renders "—", never 0.

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { BookRow, BookSortKey } from '../../lib/advertisingHome/bookRows';
import type { HomeAlert } from '../../lib/advertisingHome/alerts';
import { laneIsTinted, resolveLanes, type LaneKey } from '../../lib/advertisingHome/channelLanes';
import type { AdChannel } from '../../types/advertisingHome';
import { StatusPopover } from './StatusPopover';
import type { FxRateSnapshot } from '../../types/fxRates';
import type { CurrencyAmount } from '../../types/reporting';
import { computeDelta, deltaColorClass, type HeroDelta } from '../reporting/heroCards';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { ChannelChip } from './AlertBadges';
import { accountHref, googleReportingHref, metaReportingHref } from './deepLinks';
import { formatLeads, homeBlendedCpl, homeMoney } from './homeMoney';

/** THE ONE RULE's cell (noLeads.ts): red "No leads" with the spend, never "—" or $0. */
function NoLeadsCell({
  spend,
  display,
  snapshot,
}: {
  spend: CurrencyAmount[];
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
}): React.JSX.Element {
  return (
    <span className="font-medium text-red-700" data-testid="no-leads-cell">
      No leads
      <span className="block text-[10px] font-normal">
        {homeMoney(spend, display, snapshot)} spent
      </span>
    </span>
  );
}

/** Tiny inline SVG sparkline — the table can carry 100+ rows, so no recharts. */
function Sparkline({ points }: { points: Array<{ date: string; leads: number }> }): React.JSX.Element {
  if (points.length < 2) return <span className="text-xs text-muted-foreground">—</span>;
  const w = 72;
  const h = 20;
  const max = Math.max(...points.map((p) => p.leads), 1);
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - (p.leads / max) * (h - 2) - 1).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="text-kyck-500" aria-hidden>
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function DeltaChip({ delta, tone }: { delta: HeroDelta | null; tone: 'good-up' | 'good-down' }) {
  if (!delta) return null;
  return (
    <span className={cn('ml-1 text-[11px] tabular-nums', deltaColorClass(tone, delta.direction))}>
      {delta.pct > 0 ? '+' : ''}
      {delta.pct}%
    </span>
  );
}

function pacingText(row: BookRow): string {
  const parts: string[] = [];
  if (row.pacingMeta) parts.push(`M ${Math.round(row.pacingMeta.pace * 100)}%`);
  if (row.pacingGoogle) parts.push(`G ${Math.round(row.pacingGoogle.pace * 100)}%`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

interface Column {
  key: BookSortKey;
  label: string;
  align?: 'right';
  /** In a tinted column family (see GROUP_TINT). */
  tint?: boolean;
  /** First column of its family — carries the separating hairline. */
  edge?: boolean;
}

/**
 * ⚠️ THE GROUP TINT IS TRANSLUCENT ON PURPOSE, and that is what makes it work
 * in both of the states it has to survive:
 *
 *  · ROW HOVER — an OPAQUE cell background would sit ON TOP of the row's
 *    `hover:bg-accent/40` and swallow it, so hovering a row would highlight
 *    only the untinted columns. A translucent tint COMPOSITES over whatever the
 *    row is painting, so the hover reads across the whole row.
 *  · THE STICKY HEADER — the header cells carry the same tint over the thead's
 *    own `bg-card`, so the group banding stays aligned with the body while the
 *    header floats, and the composite is still opaque enough to hide the rows
 *    scrolling underneath.
 *
 * Alternating (Meta tinted · Google plain · Blended tinted) plus a hairline on
 * each group's first column: a tint, never a block of colour.
 */
const GROUP_TINT = 'bg-muted/40';
const GROUP_EDGE = 'border-l border-border';

/**
 * ⚠️⚠️ A KEYED LOOKUP PER LANE, NEVER A FLAT ARRAY READ BY INDEX.
 *
 * This was nine columns in one array, consumed as `.slice(0,3)` meta,
 * `.slice(3,6)` google, `.slice(6,9)` blended. The obvious way to hide a lane —
 * filter the array — SHIFTS EVERY SLICE and silently renders the wrong columns
 * under the right headers, which is worse than the em-dashes it set out to fix.
 * Keyed, a lane that is not rendered is simply never looked up, and each body
 * cell takes its banding from the SAME lane object the header was built from.
 */
/**
 * ⚠️ THE LANE'S COLUMNS ARE A LIST, NOT A FIXED TRIPLE, since §11d gave the
 * Meta lane two more. Still a KEYED LOOKUP: each lane owns its own
 * {sortKey,label} pairs and its body cells render from the SAME lane object the
 * header was built from, so there is no index arithmetic to shift.
 */
type LaneColumnSpec = { key: BookSortKey; label: string };

const BASE_LANE_COLUMNS: Record<LaneKey, readonly LaneColumnSpec[]> = {
  meta: [
    { key: 'metaSpend', label: 'Spend' },
    { key: 'metaLeads', label: 'Leads' },
    { key: 'metaCpl', label: 'CPL' },
  ],
  google: [
    { key: 'googleSpend', label: 'Spend' },
    { key: 'googleLeads', label: 'Leads' },
    { key: 'googleCpl', label: 'CPL' },
  ],
  blended: [
    { key: 'blendedSpend', label: 'Spend' },
    { key: 'blendedLeads', label: 'Leads' },
    { key: 'blendedCpl', label: 'CPL' },
  ],
};

/**
 * §11d — the four lead columns Scott asked for, in his order:
 * Meta leads · cost per Meta lead · Leads · cost per lead. `Spend` still leads
 * them, because it is the denominator context for the last pair.
 *
 * ⚠️⚠️ "Meta leads" AND "Leads" ARE NOT THE SAME METRIC, AND THE GROUP
 * HEADER SAYS SO. "Meta leads" is the on-Meta instant-form action; "Leads" is
 * `total_leads`, Meta's grouped figure, which also contains website/pixel leads
 * AND an attribution residual belonging to neither — so `Leads` minus
 * `Meta leads` is NOT website leads, and a reader left to infer it would get a
 * quantity that does not exist.
 */
const META_LEAD_DETAIL_COLUMNS: readonly LaneColumnSpec[] = [
  { key: 'metaSpend', label: 'Spend' },
  { key: 'metaFormLeads', label: 'Meta leads' },
  { key: 'metaFormCpl', label: 'Cost / Meta lead' },
  { key: 'metaLeads', label: 'Leads' },
  { key: 'metaCpl', label: 'CPL' },
];

interface Lane {
  key: LaneKey;
  /** Already carrying this lane's banding; the body renders from these. */
  columns: Column[];
}

function laneColumnSpecs(key: LaneKey, metaLeadDetail: boolean): readonly LaneColumnSpec[] {
  return key === 'meta' && metaLeadDetail ? META_LEAD_DETAIL_COLUMNS : BASE_LANE_COLUMNS[key];
}

function buildLanes(channels: readonly AdChannel[], metaLeadDetail: boolean): Lane[] {
  return resolveLanes(channels).map((key, laneIndex) => {
    const tint = laneIsTinted(laneIndex);
    const columns = laneColumnSpecs(key, metaLeadDetail).map((spec, i) => ({
      key: spec.key,
      label: spec.label,
      align: 'right' as const,
      tint,
      // The family's first column carries the separating hairline.
      edge: i === 0,
    }));
    return { key, columns };
  });
}

/** The group classes for one column, shared by its header and its body cell. */
function groupClass(col: Pick<Column, 'tint' | 'edge'>): string {
  return cn(col.tint && GROUP_TINT, col.edge && GROUP_EDGE);
}

export function BookTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  showPacing,
  display,
  snapshot,
  alertsByAccount,
  channels,
  metaLeadDetail = false,
  popoverLinkIcons = false,
  linkKeys,
}: {
  rows: BookRow[];
  sortKey: BookSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: BookSortKey) => void;
  showPacing: boolean;
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
  /** ⚠️ ALL alerts per account — the status chips' own inputs, never the
   *  panel-filtered subset (the table is ground truth). */
  alertsByAccount: Map<string, HomeAlert[]>;
  /**
   * ⚠️⚠️ REQUIRED, so a board added later cannot inherit three lanes by
   * omission — which is exactly how the specialist pages shipped, with a column
   * of em-dashes beside a Blended lane duplicating the one channel. The AM
   * board passes both and keeps Blended; a specialist board passes its one and
   * Blended is SUPPRESSED rather than relabelled (see channelLanes.ts).
   */
  channels: readonly AdChannel[];
  /**
   * Round 2 item 3 — the account's external link icons in the status
   * popover's header. ⚠️ OPT-IN; only Advertising Home passes it until the
   * density has been looked at (the Meta/Google boards share this table).
   */
  popoverLinkIcons?: boolean;
  /**
   * When set, the account-name column shows ONLY these links' icons and the
   * status popover shows the same set. Absent = Advertising Home, unchanged.
   */
  linkKeys?: AccountLinkKey[];
  /**
   * §11d — widen the Meta lane to the four lead columns.
   *
   * ⚠️ OPTIONAL and DEFAULT FALSE, so Advertising Home and the Google board are
   * byte-identical. The Meta board gates it on the breakdown having ARRIVED
   * rather than on which board is rendering: turning the columns on without the
   * data would print a column of em-dashes, which is the §9c defect again.
   */
  metaLeadDetail?: boolean;
}): React.JSX.Element {
  const lanes = buildLanes(channels, metaLeadDetail);
  const header = (col: Column) => (
    <th
      key={col.key}
      className={cn(
        'whitespace-nowrap px-2 py-1.5 font-medium',
        col.align === 'right' && 'text-right',
        groupClass(col),
      )}
    >
      <button
        type="button"
        onClick={() => onSort(col.key)}
        className="inline-flex items-center gap-0.5 hover:text-foreground"
      >
        {col.label}
        {sortKey === col.key &&
          (sortDir === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden />
          ))}
      </button>
    </th>
  );

  /**
   * One lane's body cells, rendered BY COLUMN KEY off the lane object the header
   * was built from. ⚠️ Keyed rather than positional: §11d made the Meta lane five
   * columns wide while the others stay three, and index arithmetic would put the
   * wrong figures under the right headers — worse than the em-dashes §9c set out
   * to fix.
   */
  const channelCells = (row: BookRow, lane: Lane) => {
    const cell = lane.key === 'meta' ? row.meta : row.google;
    const base = 'px-2 py-1.5 text-right';
    const value = (key: BookSortKey): React.ReactNode => {
      switch (key) {
        case 'metaSpend':
        case 'googleSpend':
          return homeMoney(cell.spend, display, snapshot);
        case 'metaLeads':
        case 'googleLeads':
          return formatLeads(cell.leads);
        case 'metaCpl':
        case 'googleCpl':
          return cell.verdict.kind === 'no_leads' ? (
            <NoLeadsCell spend={cell.spend} display={display} snapshot={snapshot} />
          ) : (
            homeBlendedCpl(cell.spend, cell.leads, cell.cpl, display, snapshot)
          );
        // §11d — the lead-ACTION axis. ⚠️ No breakdown supplied, or no Meta
        // rows for this account, renders "—" and never 0.
        case 'metaFormLeads':
          return row.metaLead ? formatLeads(row.metaLead.metaLeads) : '—';
        case 'metaFormCpl':
          // ⚠️⚠️ THE SCOPED NUMERATOR. `metaLeadSpend` is spend on on-Meta
          // instant-form lead campaigns ONLY — passing `cell.spend` here would
          // charge those leads for traffic and engagement budget and inflate the
          // figure. That is the §4b error §10 recorded, and it is why the two
          // amounts are separate fields.
          return row.metaLead
            ? homeBlendedCpl(
                row.metaLead.metaLeadSpend,
                row.metaLead.metaLeads,
                row.metaLead.costPerMetaLead,
                display,
                snapshot,
              )
            : '—';
        default:
          return '—';
      }
    };
    return (
      <>
        {lane.columns.map((col) => (
          <td
            key={col.key}
            className={cn(
              base,
              cell.active ? 'tabular-nums' : 'text-muted-foreground',
              groupClass(col),
            )}
          >
            {cell.active ? value(col.key) : '—'}
          </td>
        ))}
      </>
    );
  };

  const blendedCells = (row: BookRow, lane: Lane, leadsDelta: HeroDelta | null) => {
    // The blended lane is always the base triple: a Meta-only metric has no
    // blended meaning, so there is no §11d variant of it.
    const [spendCol, leadsCol, cplCol] = lane.columns as [Column, Column, Column];
    return (
      <>
        <td className={cn('px-2 py-1.5 text-right tabular-nums', groupClass(spendCol))}>
          {homeMoney(row.blendedSpend, display, snapshot)}
        </td>
        <td className={cn('px-2 py-1.5 text-right tabular-nums', groupClass(leadsCol))}>
          {formatLeads(row.blendedLeads)}
          <DeltaChip delta={leadsDelta} tone="good-up" />
        </td>
        <td className={cn('px-2 py-1.5 text-right tabular-nums', groupClass(cplCol))}>
          {row.blendedVerdict.kind === 'no_leads' ? (
            <NoLeadsCell spend={row.blendedSpend} display={display} snapshot={snapshot} />
          ) : (
            homeBlendedCpl(row.blendedSpend, row.blendedLeads, row.blendedCpl, display, snapshot)
          )}
        </td>
      </>
    );
  };

  /** One lane's group header — the chip for a channel, the word for Blended. */
  const laneHeader = (lane: Lane) => (
    <th
      key={lane.key}
      className={cn(
        'px-2 py-1.5 text-center font-medium',
        lane.key === 'blended' && 'text-[11px] font-semibold uppercase tracking-wide',
        // Every lane has at least one column (resolveLanes never emits an
        // empty one), so the family's banding is taken from its first.
        groupClass(lane.columns[0] ?? {}),
      )}
      colSpan={lane.columns.length}
    >
      {lane.key === 'blended' ? (
        'Blended'
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <ChannelChip channel={lane.key} />
          {/* §11d — THE GROUP HEADER NAMES THE AXIS. "Meta leads" and
              "Leads" are different metrics sitting side by side; without this a
              reader subtracts one from the other and gets a quantity that does
              not exist. */}
          {lane.key === 'meta' && lane.columns.length > 3 && (
            <span
              className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground"
              title="Meta leads = the on-Meta instant form. Leads = Meta's grouped total, which also contains website/pixel leads and an attribution residual belonging to neither — so Leads minus Meta leads is NOT website leads."
            >
              by lead type
            </span>
          )}
        </span>
      )}
    </th>
  );

  return (
    // The SearchTermsPanel sticky-header pattern: the max-h makes the wrapper a
    // real vertical scrollport, without which sticky top-0 never sticks.
    <div className="max-h-[75vh] overflow-x-auto overflow-y-auto rounded-lg border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground shadow-[inset_0_-1px_0_hsl(var(--border))]">
          <tr>
            <th className="px-3 py-1.5 text-left font-medium">
              <button type="button" onClick={() => onSort('name')} className="hover:text-foreground">
                Account
              </button>
            </th>
            {/* The group headers carry the family's tint + edge, so the banding
                reads as one block from header to last row. */}
            {lanes.map(laneHeader)}
            <th className="px-2 py-1.5 text-right font-medium">
              <button type="button" onClick={() => onSort('cplChange')} className="hover:text-foreground">
                Trend
              </button>
            </th>
            <th className="px-2 py-1.5 font-medium">Daily leads</th>
            {showPacing && (
              <th className="px-2 py-1.5 text-right font-medium">
                <button type="button" onClick={() => onSort('pacing')} className="hover:text-foreground">
                  Pacing
                </button>
              </th>
            )}
            <th className="px-2 py-1.5 font-medium">
              <button type="button" onClick={() => onSort('attention')} className="hover:text-foreground">
                Status
              </button>
            </th>
          </tr>
          <tr>
            <th className="px-3 py-1 text-left font-normal text-[10px] uppercase tracking-wide">
              name
            </th>
            {lanes.flatMap((lane) => lane.columns).map(header)}
            <th />
            <th />
            {showPacing && <th />}
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => {
            const leadsDelta = computeDelta(row.blendedLeads, row.leadsPrevious);
            const cplDelta =
              row.cplCurrent !== null && row.cplPrevious !== null
                ? computeDelta(row.cplCurrent, row.cplPrevious)
                : null;
            return (
              <tr key={row.account.id} className="hover:bg-accent/40">
                <td className="max-w-[16rem] px-3 py-1.5">
                  {linkKeys ? (
                    <span className="flex min-w-0 items-center gap-1">
                      <Link
                        to={accountHref(row.account.id)}
                        className="min-w-0 truncate font-medium hover:underline"
                        title={row.account.name}
                      >
                        {row.account.name}
                      </Link>
                      <AccountLinkIcons accountId={row.account.id} className="shrink-0" only={linkKeys} />
                    </span>
                  ) : (
                    <Link
                      to={accountHref(row.account.id)}
                      className="block truncate font-medium hover:underline"
                      title={row.account.name}
                    >
                      {row.account.name}
                    </Link>
                  )}
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    {row.account.brandName && <span className="truncate">{row.account.brandName}</span>}
                    <Link to={metaReportingHref(row.account.id)} className="shrink-0 hover:underline">
                      Meta ↗
                    </Link>
                    <Link to={googleReportingHref(row.account.id)} className="shrink-0 hover:underline">
                      Google ↗
                    </Link>
                  </span>
                </td>
                {lanes.map((lane) => (
                  <Fragment key={lane.key}>
                    {lane.key === 'blended'
                      ? blendedCells(row, lane, leadsDelta)
                      : channelCells(row, lane)}
                  </Fragment>
                ))}
                <td className="px-2 py-1.5 text-right">
                  <DeltaChip delta={cplDelta} tone="good-down" />
                  {!cplDelta && <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-2 py-1.5">
                  <Sparkline points={row.spark} />
                </td>
                {showPacing && (
                  <td className="px-2 py-1.5 text-right text-xs tabular-nums">{pacingText(row)}</td>
                )}
                <td className="px-2 py-1.5">
                  {/* The chip is the ANSWER; the popover is the reasoning +
                      where to go next. It lists EVERY alert for the row, since
                      that is what the status was computed from. */}
                  <StatusPopover
                    account={row.account}
                    health={row.health}
                    alerts={alertsByAccount.get(row.account.id) ?? []}
                    showLinkIcons={popoverLinkIcons || linkKeys !== undefined}
                    linkKeys={linkKeys}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">
          No accounts in this scope.
        </p>
      )}
    </div>
  );
}
